import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  Error,
  HStack,
  VStack,
} from "../../components/utils";
import { Input, Label, TextArea } from "../../components/shared/InputField";
import {
  useCreateSecurityClass,
  useUpdateSecurityClass,
} from "../../queries/securityClass";
import { Select } from "../../components/shared/Select";
import { getCurrencySymbol } from "../../utils/currencyFormatter";

export type SecurityClass = {
  id?: string;
  securityName: string;
  securityType?: string;
  securityClass: string;
  isin: string;
  faceValue: number;
  currency: string;
  issueDate?: Date | string;
  endDate?: Date | string;
  companyId: string;
  notes: string;
};

type AddOrEditSecurityClassProps = {
  mode: "Edit" | "Add";
  data?: SecurityClass;
  onClose: () => void;
};

function AddOrEditSecurityClass(props: AddOrEditSecurityClassProps) {
  const { data, mode, onClose } = props;
  const currencySymbol = getCurrencySymbol();

  const initialValues: SecurityClass = {
    securityName: data?.securityName || "",
    securityType: data?.securityType || "",
    securityClass: data?.securityClass || "",
    isin: data?.isin || "",
    faceValue: data?.faceValue || 0,
    currency: data?.currency || "",
    issueDate: data?.issueDate
      ? new Date(data.issueDate).toISOString().slice(0, 10)
      : "",
    endDate: data?.endDate
      ? new Date(data.endDate).toISOString().slice(0, 10)
      : "",
    companyId: data?.companyId || "",
    notes: data?.notes || "",
    id: data?.id,
  };

  const validationSchema = Yup.object().shape({
    securityName: Yup.string().required("Security Name is required"),
    securityType: Yup.string(),
    securityClass: Yup.string().required("Security Class is required"),
    isin: Yup.string().required("ISIN is required"),
    faceValue: Yup.number()
      .required("Face Value is required")
      .moreThan(0, "Face Value must be greater than 0"),
    notes: Yup.string(),
  });

  const { mutate: addSecurityClass, isLoading: isAddLoading } =
    useCreateSecurityClass();
  const { mutate: editSecurityClass, isLoading: isEditLoading } =
    useUpdateSecurityClass();

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      if (mode === "Add") {
        addSecurityClass(values, {
          onSuccess: () => {
            setSubmitting(false);
            onClose();
          },
          onError: (err: any) => {
            setSubmitting(false);
            toast(
              err?.response?.data?.reason || "Failed to add security class"
            );
          },
        });
      } else {
        editSecurityClass(values, {
          onSuccess: () => {
            setSubmitting(false);
            onClose();
          },
          onError: (err: any) => {
            setSubmitting(false);
            toast(
              err?.response?.data?.reason || "Failed to update security class"
            );
          },
        });
      }
    },
  });

  return (
    <Box className="text-lg font-medium border-b py-7">
      <h6 className="flex justify-between px-4 pb-4 border-b">
        {mode === "Edit" ? "Edit Security Class" : "Add Security Class"}
        <span onClick={onClose} className="cursor-pointer">
          ✕
        </span>
      </h6>
      <VStack className="w-full gap-4 px-4 pt-4">
        <HStack className="items-center gap-4">
          <div className="flex-1">
            <Label>Security Name</Label>
            <Input type="text" {...formik.getFieldProps("securityName")} />
            {formik.touched.securityName && formik.errors.securityName && (
              <Error text={formik.errors.securityName as string} />
            )}
          </div>
          <div className="flex-1">
            <Label>Security Type</Label>
            <Select
              options={["EQUITY", "CCPS"]}
              {...formik.getFieldProps("securityType")}
            />
            {formik.touched.securityType && formik.errors.securityType && (
              <Error text={formik.errors.securityType as string} />
            )}
          </div>
        </HStack>
        <HStack className="items-center gap-4">
          <div className="flex-1">
            <Label>Security Class</Label>
            <Input type="text" {...formik.getFieldProps("securityClass")} />
            {formik.touched.securityClass && formik.errors.securityClass && (
              <Error text={formik.errors.securityClass as string} />
            )}
          </div>
          <div className="flex-1">
            <Label>ISIN</Label>
            <Input type="text" {...formik.getFieldProps("isin")} />
            {formik.touched.isin && formik.errors.isin && (
              <Error text={formik.errors.isin as string} />
            )}
          </div>
        </HStack>
        <HStack className="items-center gap-4">
          <div className="flex-1">
            <Label>Face Value {currencySymbol}</Label>
            <Input type="number" {...formik.getFieldProps("faceValue")} />
            {formik.touched.faceValue && formik.errors.faceValue && (
              <Error text={formik.errors.faceValue as string} />
            )}
          </div>
          <div className="flex-1"></div>
        </HStack>
        <HStack className="items-center gap-4">
          <div className="flex-1">
            <Label>Notes</Label>
            <TextArea {...formik.getFieldProps("notes")} />
            {formik.touched.notes && formik.errors.notes && (
              <Error text={formik.errors.notes as string} />
            )}
          </div>
        </HStack>
        <HStack className="justify-between pt-6">
          <ButtonPrimary1 onClick={onClose}>Cancel</ButtonPrimary1>
          <ButtonPrimary
            type="submit"
            onClick={() => formik.handleSubmit()}
            loading={isAddLoading || isEditLoading}
          >
            Save
          </ButtonPrimary>
        </HStack>
      </VStack>
    </Box>
  );
}

export default AddOrEditSecurityClass;
